import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export interface TagChipProps {
  name: string;
  color: string;
  onRemove?: () => void;
  className?: string;
}

/**
 * Tag chip built on shadcn `Badge`. Background is the tag color at low alpha
 * for readability against any list-row surface, with a small filled dot
 * carrying the full saturation.
 */
export const TagChip = ({ name, color, onRemove, className }: TagChipProps) => {
  const background = withAlpha(color, 0.18);

  return (
    <Badge
      variant="ghost"
      className={cn('gap-1 font-medium', className)}
      style={{ background, color }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} aria-hidden="true" />
      <span>{name}</span>
      {onRemove ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="ml-0.5 cursor-pointer leading-none opacity-60 hover:opacity-100"
          aria-label={`Remove ${name}`}
        >
          ×
        </button>
      ) : null}
    </Badge>
  );
};

const withAlpha = (hex: string, alpha: number): string => {
  // Accept #rrggbb or #rrggbbaa; ignore alpha if already present.
  const rgb = /^#([0-9a-f]{6})/i.exec(hex)?.[1];
  if (!rgb) return hex;
  const r = parseInt(rgb.slice(0, 2), 16);
  const g = parseInt(rgb.slice(2, 4), 16);
  const b = parseInt(rgb.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
